// Vue
import { createApp, watch } from "vue";
// Dependencies
import { Frontegg } from "@frontegg/vue";
import { createPinia, storeToRefs } from "pinia";
import VueDragscroll from "vue-dragscroll";
import { DefaultApolloClient, provideApolloClient } from "@vue/apollo-composable";
import * as Sentry from "@sentry/vue";
import i18n from "@/services/i18n";

// Services
import FontAwesomeIcon from "./services/Utils/fa-icons/library";
import fronteggOptions from "./services/fronteggOptions";
import vClickOutside from "./services/CustomDirectives/clickOutside";
import router from "./router/router";

import Toast, { POSITION } from "vue-toastification";

// Components
import App from "./App.vue";

// Providers
import gaiupApolloClient from "./graphql/services/ApolloClient";

import tooltipDirective from "@/directives/tooltipDirective";

import { installGuards } from "@/router/router";

// Styles¨
import "github-markdown-css/github-markdown.css";
import "./style.css";
import "./transitions.css";
import "./toasts.scss";
import { useAuthStateStore } from "./stores/Utils/auth/AuthState";
import { isFronteggTransientError, isSwitchingTenant } from "./state/tenantSwitch";
import { STALE_ASSET_MESSAGES, recoverFromStaleAsset } from "./services/staleAssetRecovery";

const pinia = createPinia();

const app = createApp(App);
app.provide(DefaultApolloClient, gaiupApolloClient);

// Frontegg's internal entitlements computed briefly reads `user` on undefined
// while `switchTenant` propagates. The pattern is narrow enough that we always
// suppress it — when Frontegg surfaces a real auth error it has a different
// shape. Other errors still go to Sentry/console.
app.config.errorHandler = (err, _instance, info) => {
  if (isFronteggTransientError(err)) return;
  Sentry.captureException(err, { extra: { vueInfo: info } });
  console.error("[Vue]", err, info);
};

// Capture-phase so we run before browser default logging.
window.addEventListener(
  "unhandledrejection",
  (event) => {
    if (isFronteggTransientError(event.reason)) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  },
  true
);

window.addEventListener(
  "error",
  (event) => {
    if (isFronteggTransientError(event.error ?? event.message)) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  },
  true
);

// Vite raises this when a dynamic import's chunk vanished with the last deploy, whether the router
// or a component asked for it. The router's own onError only ever sees the former.
window.addEventListener("vite:preloadError", (event) => {
  if ("payload" in event) recoverFromStaleAsset(event.payload);
});

// Reference isSwitchingTenant so the import is retained (it's still used by
// the dropdown to drive UI). Keeping it eslint-disable-friendly without the
// extra check inside the handlers above.
void isSwitchingTenant;

provideApolloClient(gaiupApolloClient);
// tracesSampleRate while testing set to 1.0

const vercelEnv = import.meta.env.VERCEL_ENV;
const sentryEnv = import.meta.env.VITE_SENTRY_ENV ?? (vercelEnv === "preview" ? "staging" : vercelEnv) ?? import.meta.env.MODE;

Sentry.init({
  app,
  dsn: "https://da870c7928bce5f32a250c7f40cad4c5@o4509082235895808.ingest.de.sentry.io/4509082237599824",

  integrations: [
    // pass router so transactions use route names (e.g. "Project.Dashboard") instead of raw paths
    Sentry.browserTracingIntegration({ router }),
    Sentry.replayIntegration({
      maskAllText: true,
      blockAllMedia: sentryEnv === "production",
      // capture request/response bodies for the Carbon REST API to aid debugging
      // covers prod (api.realtimelca.com) + dev/staging (api.api-dev.realtimelca.com)
      // Authorization/Cookie/Set-Cookie are stripped by Sentry regardless
      // Frontegg, Hasura GraphQL, the AI agent service, EPD, and Speckle are intentionally excluded
      // ref: https://docs.sentry.io/platforms/javascript/session-replay/configuration/#network-details
      networkDetailAllowUrls: [/^https:\/\/api\.(api-dev\.)?realtimelca\.com\/rest\/api\//],
      // additional headers to capture beyond Sentry's safe defaults — opportunistic, no-op if absent
      // matching is case-insensitive
      networkRequestHeaders: ["X-Request-Id", "X-Correlation-Id"],
      networkResponseHeaders: [
        "X-Request-Id",
        "X-Correlation-Id",
        "X-RateLimit-Remaining",
        "X-RateLimit-Reset",
        "X-Build-Id",
        "X-Server-Version",
      ],
    }),
    Sentry.feedbackIntegration({
      autoInject: false,
      colorScheme: "light",
      showName: false,
      showEmail: false,
      enableScreenshot: true,
      formTitle: "Send feedback",
      submitButtonLabel: "Send",
      messagePlaceholder: "What's on your mind?",
      themeLight: {
        accentBackground: "#2036ff",
        accentForeground: "#ffffff",
        background: "#ffffff",
        foreground: "#1e293b",
        successColor: "#6aae34",
        errorColor: "#ee4745",
      },
    }),
  ],

  // tracing
  tracesSampleRate: sentryEnv === "production" ? 0.1 : 1.0,

  // drop known noise that isn't actionable
  ignoreErrors: [
    "ResizeObserver loop limit exceeded",
    "ResizeObserver loop completed with undelivered notifications",
    "Non-Error promise rejection captured",
    /^Loading chunk \d+ failed/,
    /^ChunkLoadError/,
    "Network request failed",
    // handled by recoverFromStaleAsset; it reports on its own when the reload does not help
    ...STALE_ASSET_MESSAGES,
    // Detached Speckle traversal promise outliving disposeViewer(); see DashboardViewer's unmount hook.
    "DefermentManager is disposed",
  ],

  // drop errors originating in third-party scripts (extensions, analytics) — not our bugs
  denyUrls: [/chrome-extension:\/\//, /moz-extension:\/\//, /safari-extension:\/\//, /googletagmanager\.com/],

  // last-line PII scrubber — strips access_token / id_token / code from URLs and query strings
  beforeSend(event) {
    const SENSITIVE_QUERY_KEYS = ["access_token", "id_token", "refresh_token", "code", "token", "apiKey"];
    const scrubUrl = (url: string | undefined): string | undefined => {
      if (!url) return url;
      try {
        const u = new URL(url, window.location.origin);
        for (const key of SENSITIVE_QUERY_KEYS) {
          if (u.searchParams.has(key)) u.searchParams.set(key, "[Filtered]");
        }
        return u.toString();
      } catch {
        return url;
      }
    };
    if (event.request) {
      event.request.url = scrubUrl(event.request.url);
      if (typeof event.request.query_string === "string") {
        event.request.query_string = scrubUrl(`?${event.request.query_string}`)?.replace(/^.*?\?/, "");
      }
    }

    // Speckle's render loop outlives disposeViewer() (speckle.store.ts) and eventually renders into a
    // lost context; three.js's WebGLProgram then calls .trim() on a null getProgramInfoLog().
    const exception = event.exception?.values?.[0];
    if (exception?.value?.includes("trim") && exception.stacktrace?.frames?.some((frame) => frame.function === "WebGLProgram")) {
      return null;
    }

    // Default grouping folds every AxiosError into one issue; fingerprint on status + the last in-app
    // frame so a 503 on `projects` and a 403 on `skills` land in separate issues.
    if (exception?.type === "AxiosError") {
      const status = exception.value?.match(/status code (\d+)/)?.[1] ?? "unknown";
      const frames = exception.stacktrace?.frames ?? [];
      const culprit = [...frames].reverse().find((frame) => frame.in_app)?.function ?? event.transaction ?? "unknown";
      event.fingerprint = ["axios-error", status, culprit];
    }

    return event;
  },

  // this is what shows up in Sentry filters
  environment: sentryEnv,
  release: import.meta.env.VITE_SENTRY_RELEASE ?? "unknown",

  // enabled everywhere — feedback widget requires Sentry to be active
  enabled: true,

  // replays
  replaysSessionSampleRate: sentryEnv === "staging" ? 0.3 : 0.0,
  replaysOnErrorSampleRate: sentryEnv === "production" || sentryEnv === "staging" ? 1.0 : 0.0,
});

// Register components and directives
app.component("FontAwesomeIcon", FontAwesomeIcon);
app.directive("click-outside", vClickOutside);

// Register plugins
app.use(pinia);
app.use(router);

// Install router guards after Pinia and router are registered
installGuards(router);

app.use(Frontegg, {
  ...fronteggOptions,
  authOptions: {
    keepSessionAlive: true,
  },
  /*   hostedLoginBox: false, */
  /* Uncomment if login is hosted */
  /* hostedLoginBox: true, */
  router,
});

app.use(i18n);
app.use(VueDragscroll);
app.directive("tooltip", tooltipDirective);
app.use(Toast, {
  position: POSITION.TOP_CENTER,
  accessibility: { toastRole: "alert", closeButtonLabel: "Close notification" },
});

const auth = useAuthStateStore(pinia);
const { user } = storeToRefs(auth);

watch(
  user,
  (newUser) => {
    if (newUser) {
      Sentry.setUser({
        id: String(newUser.id),
        tenantId: newUser.tenantId,
      });
      // tags are searchable as filters in the Sentry UI, unlike user fields
      Sentry.setTag("tenantId", newUser.tenantId);
    } else {
      Sentry.setUser(null); // clear on logout
      Sentry.setTag("tenantId", undefined);
    }
  },
  { immediate: true }
);

// Mount the app
app.mount("#app");
